/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft,
  faRocket,
  faLightbulb,
  faGlobe,
  faChartLine,
  faGraduationCap,
  faHeart,
  faUsers,
  faCode,
  faPalette,
  faBriefcase,
  faMapMarkerAlt,
  faClock,
  faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import '../styles/Careers.css';

const Careers = () => {
  const navigate = useNavigate();
  const [selectedDepartment, setSelectedDepartment] = useState('All');

  const departments = ['All', 'Engineering', 'Design', 'Product', 'Marketing', 'Sales', 'HR'];

  const jobOpenings = [
    {
      id: 1,
      title: 'Senior Full Stack Developer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Help us build the next generation of our job matching platform using React, Node.js, and Python. Work with cutting-edge AI technologies.',
      icon: faCode,
      color: 'orange'
    },
    {
      id: 2,
      title: 'Product Manager',
      department: 'Product',
      location: 'Remote',
      type: 'Full-time',
      description: 'Drive product strategy and roadmap for our AI-powered recruitment solutions. Shape the future of how people find jobs.',
      icon: faBriefcase,
      color: 'teal'
    },
    {
      id: 3,
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Remote',
      type: 'Full-time',
      description: 'Design intuitive and beautiful user experiences for job seekers and employers. Create designs that make a difference.',
      icon: faPalette,
      color: 'orange'
    },
    {
      id: 4,
      title: 'Frontend Developer',
      department: 'Engineering',
      location: 'Remote',
      type: 'Full-time',
      description: 'Build responsive and performant user interfaces using React and modern web technologies.',
      icon: faCode,
      color: 'teal'
    },
    {
      id: 5,
      title: 'Marketing Manager',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time',
      description: 'Lead our marketing efforts to grow AksharJobs brand awareness and user acquisition.',
      icon: faChartLine,
      color: 'orange'
    },
    {
      id: 6,
      title: 'HR Specialist',
      department: 'HR',
      location: 'Remote',
      type: 'Full-time',
      description: 'Support our growing team with recruitment, onboarding, and employee engagement initiatives.',
      icon: faUsers,
      color: 'teal'
    }
  ];

  const filteredJobs = selectedDepartment === 'All' 
    ? jobOpenings 
    : jobOpenings.filter(job => job.department === selectedDepartment);

  const benefits = [
    {
      icon: faRocket,
      title: 'Cutting-Edge Technology',
      description: 'Work with the latest AI and machine learning technologies',
      color: 'orange'
    },
    {
      icon: faLightbulb,
      title: 'Innovation Culture',
      description: 'Your ideas matter - we encourage creativity and innovation',
      color: 'teal'
    },
    {
      icon: faGlobe,
      title: 'Remote-First',
      description: 'Work from anywhere with flexible working hours',
      color: 'orange'
    },
    {
      icon: faChartLine,
      title: 'Career Growth',
      description: 'Clear career progression paths and development opportunities',
      color: 'teal'
    },
    {
      icon: faGraduationCap,
      title: 'Learning & Development',
      description: 'Continuous learning budget and training programs',
      color: 'orange'
    },
    {
      icon: faHeart,
      title: 'Health & Wellness',
      description: 'Comprehensive health benefits and wellness programs',
      color: 'teal'
    }
  ];

  const values = [
    {
      title: 'Innovation',
      description: 'We constantly push boundaries to create better solutions for job seekers and employers.',
      emoji: '🚀'
    },
    {
      title: 'Collaboration',
      description: 'We believe in the power of teamwork and diverse perspectives.',
      emoji: '🤝'
    },
    {
      title: 'Impact',
      description: 'Every line of code, every design, every decision - it all makes a difference.',
      emoji: '💡'
    },
    {
      title: 'Integrity',
      description: 'We build trust through transparency, honesty, and ethical practices.',
      emoji: '✨'
    }
  ];

  return (
    <div className="careers-page">
      <button 
        className="back-to-home-btn" 
        onClick={() => navigate('/')}
        aria-label="Back to Homepage"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
        <span>Back to Home</span>
      </button>

      {/* Hero Section */}
      <section className="careers-hero">
        <div className="careers-hero-overlay"></div>
        <div className="careers-hero-content">
          <div className="hero-badge">
            <FontAwesomeIcon icon={faUsers} className="badge-icon" />
            <span>JOIN OUR TEAM</span>
          </div>
          <h1 className="careers-hero-title">
            Build Your Career at <br />
            <span className="gradient-text">AksharJobs</span>
          </h1>
          <p className="careers-hero-subtitle">
            Join a passionate team revolutionizing the way people find jobs and companies discover talent
          </p>
          <div className="hero-stats">
            <div className="stat-item">
              <div className="stat-number">50+</div>
              <div className="stat-label">Team Members</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">15+</div>
              <div className="stat-label">Countries</div>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <div className="stat-number">100%</div>
              <div className="stat-label">Remote</div>
            </div>
          </div>
        </div>
        <div className="hero-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="why-work-section">
        <div className="section-container">
          <h2 className="section-title">Why Work With Us?</h2>
          <p className="section-subtitle">
            We're building something special, and we want you to be part of it
          </p>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className={`benefit-card ${benefit.color}`}>
                <div className={`benefit-icon ${benefit.color}-icon`}>
                  <FontAwesomeIcon icon={benefit.icon} />
                </div>
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="values-section">
        <div className="section-container">
          <h2 className="section-title">Our Values</h2>
          <p className="section-subtitle">
            The principles that guide everything we do
          </p>
          
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-emoji">{value.emoji}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="positions-section">
        <div className="section-container">
          <h2 className="section-title">Open Positions</h2>
          <p className="section-subtitle">
            Find your perfect role and join our growing team
          </p>

          {/* Department Filter */}
          <div className="department-filter">
            {departments.map((dept) => (
              <button
                key={dept}
                className={`filter-btn ${selectedDepartment === dept ? 'active' : ''}`}
                onClick={() => setSelectedDepartment(dept)}
              >
                {dept}
              </button>
            ))}
          </div>

          {/* Job Listings */}
          <div className="jobs-grid">
            {filteredJobs.map((job) => (
              <div key={job.id} className={`job-card ${job.color}`}>
                <div className={`job-icon ${job.color}-icon`}>
                  <FontAwesomeIcon icon={job.icon} />
                </div>
                <h3 className="job-title">{job.title}</h3>
                <div className="job-meta">
                  <span className="job-meta-item">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />
                    {job.location}
                  </span>
                  <span className="job-meta-item">
                    <FontAwesomeIcon icon={faClock} />
                    {job.type}
                  </span>
                  <span className="job-department">{job.department}</span>
                </div>
                <p className="job-description">{job.description}</p>
                <button className={`apply-btn ${job.color}-btn`}>
                  Apply Now
                </button>
              </div>
            ))}
          </div>

          {filteredJobs.length === 0 && (
            <div className="no-jobs">
              <p>No positions available in this department at the moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Application Process Section */}
      <section className="process-section">
        <div className="section-container">
          <h2 className="section-title">Our Hiring Process</h2>
          <p className="section-subtitle">
            A transparent and straightforward journey to joining our team
          </p>
          
          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3 className="step-title">Apply</h3>
              <p className="step-description">Submit your application with your resume and cover letter</p>
            </div>
            <div className="process-connector"></div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3 className="step-title">Screen</h3>
              <p className="step-description">Initial review and phone screening with our HR team</p>
            </div>
            <div className="process-connector"></div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3 className="step-title">Interview</h3>
              <p className="step-description">Technical and cultural fit interviews with team members</p>
            </div>
            <div className="process-connector"></div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3 className="step-title">Offer</h3>
              <p className="step-description">Receive your offer and join the AksharJobs family!</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="careers-cta">
        <div className="cta-content">
          <h2>Ready to Make an Impact?</h2>
          <p>
            Don't see the perfect role? Send us your resume anyway! We're always looking for talented individuals.
          </p>
          <a href="mailto:careers@aksharjobs.com" className="cta-button">
            <FontAwesomeIcon icon={faEnvelope} />
            careers@aksharjobs.com
          </a>
        </div>
      </section>
    </div>
  );
};

export default Careers;
